import { apiBinaryRequest, apiRequest } from './apiClient';

const buildQueryString = (params) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      return;
    }
    query.set(key, String(value));
  });
  return query.toString();
};

export const fetchFilteredTimeEvents = async (caseId, options = {}) => {
  const query = buildQueryString({
    requestId: options.requestId || '',
    page: Number.isInteger(options.page) ? options.page : 1,
    pageSize: Number.isInteger(options.pageSize) ? options.pageSize : 15,
    filter: options.filter || '',
    showHidden: options.showHidden ? 'true' : 'false',
    visibilityFilter: options.visibilityFilter || '',
    sortBy: options.sortBy || 'date',
    sortOrder: options.sortOrder || 'desc',
    tagVariable: options.tagVariable || '',
    markAsRead: options.markAsRead === false ? 'false' : 'true',
    validationQueue: options.validationQueue ? 'true' : '',
    validationType: options.validationType || '',
    groupId: options.groupId || '',
  });

  return apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/timeevents${query ? `?${query}` : ''}`, {
    signal: options.signal,
  });
};

const timeEventListFrom = (payload) => (
  payload?.events || payload?.timeevents || payload?.data?.events || []
);

export const fetchAllFilteredTimeEvents = async (caseId, options = {}) => {
  const eventsById = new Map();
  const pageSize = Number.isInteger(options.pageSize) ? options.pageSize : 1000;
  let page = 1;
  let totalPages = 1;

  do {
    const payload = await fetchFilteredTimeEvents(caseId, { ...options, page, pageSize });
    timeEventListFrom(payload).forEach((event) => eventsById.set(String(event.id), event));
    const reportedTotalPages = Number(payload?.totalPages ?? payload?.data?.totalPages);
    if (Number.isFinite(reportedTotalPages)) {
      totalPages = Math.max(totalPages, Math.floor(reportedTotalPages));
    }
    page += 1;
  } while (page <= totalPages);

  return Array.from(eventsById.values());
};

export const fetchValidationQueuePage = async (caseId, options = {}) => (
  fetchFilteredTimeEvents(caseId, {
    ...options,
    page: Number.isInteger(options.page) ? options.page : 1,
    pageSize: Number.isInteger(options.pageSize) ? options.pageSize : 100,
    markAsRead: false,
    showHidden: true,
    validationQueue: true,
    validationType: options.validationType || '',
  })
);

export const fetchCalendarData = async (caseId, options = {}) => {
  const query = buildQueryString({
    maxEventsPerDay: Number.isInteger(options.maxEventsPerDay) ? options.maxEventsPerDay : 20,
    year: Number.isInteger(options.year) ? options.year : '',
    month: Number.isInteger(options.month) ? options.month : '',
  });

  return apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/calendar${query ? `?${query}` : ''}`, {
    signal: options.signal,
  });
};

export const fetchTimeeventCount = async (caseId, options = {}) => {
  return apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/timeevents/count`, {
    signal: options.signal,
  });
};

export const fetchTimeeventThumbnail = async (eventId) => {
  const response = await apiBinaryRequest(`/api/v1/timeevents/${encodeURIComponent(eventId)}/thumbnail`);
  return response.blob();
};

export const fetchTimeeventById = async (eventId) => {
  const payload = await apiRequest(`/api/v1/timeevents/${encodeURIComponent(eventId)}`);
  return payload.data ?? payload;
};

export const fetchValidationLocks = async (caseId) => {
  const payload = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/validation-locks`);
  return Array.isArray(payload?.locks) ? payload.locks : [];
};

export const fetchValidationProjectSummaries = async (caseId = null) => {
  const query = buildQueryString({ case_id: caseId });
  const payload = await apiRequest(`/api/v1/validation/project-summaries${query ? `?${query}` : ''}`);
  return Array.isArray(payload?.summaries) ? payload.summaries : [];
};

export const fetchActiveValidationLock = async () => {
  const payload = await apiRequest('/api/v1/validation/active-lock');
  return payload?.lock || null;
};

export const releaseActiveValidationLock = async (lockToken) => {
  return apiRequest('/api/v1/validation/active-lock', {
    method: 'DELETE',
    headers: validationLockHeaders(lockToken),
  });
};

export const fetchProjectTagNames = async (caseId) => {
  const payload = await apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/tags/all`);
  return Array.isArray(payload?.tags) ? payload.tags : [];
};

export const fetchProjectEventGroups = async (caseId, keyVariable = '', options = {}) => {
  const query = buildQueryString({
    keyVariable,
    page: Number.isInteger(options.page) ? options.page : 1,
    pageSize: Number.isInteger(options.pageSize) ? options.pageSize : 50,
    showHidden: options.showHidden ? 'true' : 'false',
    visibilityFilter: options.visibilityFilter || '',
  });
  return apiRequest(
    `/api/v1/projects/${encodeURIComponent(caseId)}/groups${query ? `?${query}` : ''}`,
    { signal: options.signal },
  );
};

export const createProjectGroupTag = async (caseId, groupId, tag) => (
  apiRequest(
    `/api/v1/projects/${encodeURIComponent(caseId)}/groups/${encodeURIComponent(groupId)}/tags`,
    {
      method: 'POST',
      body: JSON.stringify(tag),
    },
  )
);

export const updateProjectGroupTag = async (caseId, groupId, tagId, tag) => (
  apiRequest(
    `/api/v1/projects/${encodeURIComponent(caseId)}/groups/${encodeURIComponent(groupId)}/tags/${encodeURIComponent(tagId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(tag),
    },
  )
);

export const deleteProjectGroupTag = async (caseId, groupId, tagId) => (
  apiRequest(
    `/api/v1/projects/${encodeURIComponent(caseId)}/groups/${encodeURIComponent(groupId)}/tags/${encodeURIComponent(tagId)}`,
    { method: 'DELETE' },
  )
);

const validationLockHeaders = (lockToken) => (
  lockToken ? { 'X-Validation-Lock-Token': lockToken } : {}
);

export const updateTimeeventValidation = async (eventId, payload, options = {}) => (
  apiRequest(`/api/v1/timeevents/${encodeURIComponent(eventId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
    headers: validationLockHeaders(options.lockToken),
  })
);

export const resolveTimeeventSplitReview = async (eventId, decision, options = {}) => (
  apiRequest(`/api/v1/timeevents/${encodeURIComponent(eventId)}/split-review`, {
    method: 'PATCH',
    body: JSON.stringify({
      decision,
      ...(Array.isArray(options.documents) ? { documents: options.documents } : {}),
    }),
    headers: validationLockHeaders(options.lockToken),
  })
);

export const updateTimeeventTag = async (eventId, tag, options = {}) => (
  apiRequest(`/api/v1/timeevents/${encodeURIComponent(eventId)}/tags`, {
    method: 'PATCH',
    body: JSON.stringify({ tag, is_new_tag: Boolean(options.isNewTag) }),
    headers: validationLockHeaders(options.lockToken),
  })
);

export const deleteTimeeventTag = async (eventId, tagId, options = {}) => (
  apiRequest(
    `/api/v1/timeevents/${encodeURIComponent(eventId)}/tags/${encodeURIComponent(tagId)}`,
    { method: 'DELETE', headers: validationLockHeaders(options.lockToken) }
  )
);

// Manual reanalysis uses the authenticated HTTP API so the UI does not depend on a websocket command.
export const reanalyzeTimeevent = async (eventId) => {
  return apiRequest(`/api/v1/timeevents/${encodeURIComponent(eventId)}/reanalyze`, {
    method: 'POST',
  });
};

export const queueProjectReanalysis = async (caseId) => {
  return apiRequest(`/api/v1/projects/${encodeURIComponent(caseId)}/reanalyze`, {
    method: 'POST',
  });
};

const PROJECT_REANALYSIS_TERMINAL_STATUSES = new Set([
  'completed',
  'completed_with_errors',
  'failed',
]);

export const isProjectReanalysisTerminalStatus = (status) => (
  PROJECT_REANALYSIS_TERMINAL_STATUSES.has(status)
);

export const fetchProjectReanalysis = async (caseId, jobId) => {
  return apiRequest(
    `/api/v1/projects/${encodeURIComponent(caseId)}/reanalyze/${encodeURIComponent(jobId)}`
  );
};

export const fetchTagProof = async (tagId, options = {}) => {
  const query = buildQueryString({
    pagination_mode: options.paginationMode || 'offset',
    cursor: options.cursor ?? '',
    limit: Number.isInteger(options.limit) ? options.limit : '',
    offset: Number.isInteger(options.offset) ? options.offset : '',
    search: options.search || '',
    requestId: options.requestId || '',
  });

  const payload = await apiRequest(`/api/v1/tags/${encodeURIComponent(tagId)}/proofs${query ? `?${query}` : ''}`);
  return payload.data ?? payload;
};

export const fetchWidgetEvidence = async (widgetId, kind, options = {}) => {
  const normalizedKind = kind === 'failures' ? 'failures' : 'misses';
  const query = buildQueryString({
    caseid: options.caseId ?? '',
    requestId: options.requestId || '',
    pagination_mode: options.paginationMode || 'cursor',
    cursor: options.cursor ?? '',
    limit: Number.isInteger(options.limit) ? options.limit : '',
    offset: Number.isInteger(options.offset) ? options.offset : '',
    search: options.search || '',
  });

  const payload = await apiRequest(`/api/v1/widgets/${encodeURIComponent(widgetId)}/evidence/${normalizedKind}${query ? `?${query}` : ''}`);
  return payload.data ?? payload;
};

export const fetchWidgetEvidenceMisses = async (widgetId, options = {}) => {
  return fetchWidgetEvidence(widgetId, 'misses', options);
};

export const fetchWidgetEvidenceFailures = async (widgetId, options = {}) => {
  return fetchWidgetEvidence(widgetId, 'failures', options);
};
